import { useEffect, useRef, useState } from "react";
import {
  Archive,
  ArrowUpRight,
  Check,
  Download,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  Files,
  HardDrive,
  Image as ImageIcon,
  Link2,
  Presentation,
  QrCode,
  Share2,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { getMetas, deleteRecord } from "../lib/db.js";
import { cloudHealth, deleteCloudFile, getCloudFile, listCloudFiles } from "../lib/cloud.js";
import { mergePdfs } from "../lib/pdf.js";
import { downloadBlob, formatBytes, formatDate, kindFromName, kindLabel } from "../lib/utils.js";

const ACCEPT = ".pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.webp,.gif,.bmp,.svg,.txt,.md,.markdown,.csv";

const FORMATS = [
  { key: "pdf", label: "PDF", desc: "预览 / 转图片与 Word / 压缩 / 页面编辑", icon: FileText, tone: "rose" },
  { key: "docx", label: "Word", desc: "在线编辑 / 转 PDF / 导出 TXT / 重新打包", icon: File, tone: "blue" },
  { key: "xlsx", label: "Excel", desc: "表格编辑 / 转 CSV 与 JSON / 转 PDF", icon: FileSpreadsheet, tone: "green" },
  { key: "pptx", label: "PPT", desc: "幻灯片预览 / 文字编辑 / 转 PDF / 重新打包", icon: Presentation, tone: "amber" },
  { key: "image", label: "图片", desc: "格式转换 / 压缩 / 旋转滤镜 / 合成 PDF", icon: ImageIcon, tone: "violet" },
];

export default function Home({ onOpen, onOpenFile, onToast }) {
  const [metas, setMetas] = useState(() => getMetas());
  const [dragging, setDragging] = useState(false);
  const [merging, setMerging] = useState(false);
  const [cloudReady, setCloudReady] = useState(null);
  const [cloudFiles, setCloudFiles] = useState([]);
  const inputRef = useRef(null);

  const refresh = () => setMetas(getMetas());
  const refreshCloud = async () => {
    try {
      setCloudFiles(await listCloudFiles());
    } catch {
      setCloudFiles([]);
    }
  };

  useEffect(() => {
    let cancelled = false;
    cloudHealth()
      .then(async (ok) => {
        if (cancelled) return;
        setCloudReady(ok);
        if (ok) {
          try {
            const list = await listCloudFiles();
            if (!cancelled) setCloudFiles(list);
          } catch {
            if (!cancelled) setCloudReady(false);
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function openCloud(meta, tool) {
    try {
      const res = await getCloudFile(meta.id);
      const blob = await res.blob();
      const file = new File([blob], meta.name, { type: meta.type || "application/octet-stream" });
      await onOpenFile(file, tool || undefined);
    } catch (err) {
      onToast(err.message || "打开云端文件失败", "error");
    }
  }

  async function downloadCloud(meta) {
    try {
      const res = await getCloudFile(meta.id);
      downloadBlob(await res.blob(), meta.name);
      onToast("云端文件已开始下载", "success");
    } catch (err) {
      onToast(err.message || "下载云端文件失败", "error");
    }
  }

  async function removeCloud(meta) {
    try {
      await deleteCloudFile(meta.id);
      await refreshCloud();
      onToast(`已从云端删除 ${meta.name}`, "info");
    } catch (err) {
      onToast(err.message || "删除云端文件失败", "error");
    }
  }

  async function handleFiles(files, tool = "") {
    const list = Array.from(files || []).filter((f) => {
      const ok = kindFromName(f.name) !== "other";
      if (!ok) onToast(`${f.name} 暂不支持，仅支持 PDF / Word / Excel / PPT / 图片 / 文本`, "error");
      return ok;
    });
    if (!list.length) return;
    if (list.length === 1) {
      await onOpenFile(list[0], tool || undefined);
      refresh();
      return;
    }
    for (const file of list) {
      const { saveRecord } = await import("../lib/db.js");
      await saveRecord(file);
    }
    refresh();
    onToast(`已导入 ${list.length} 个文件到文档库`, "success");
  }

  async function remove(id, name) {
    await deleteRecord(id);
    refresh();
    onToast(`已删除 ${name}`, "info");
  }

  function openTool(tool) {
    const input = inputRef.current;
    input.dataset.tool = tool;
    input.value = "";
    input.click();
  }

  return (
    <div className="home">
      <header className="home-header">
        <div className="brand">
          <div className="brand-mark">
            <Files size={21} strokeWidth={2.4} />
          </div>
          <div>
            <h1>文档工坊</h1>
            <p>在线文档转换与编辑工作台</p>
          </div>
        </div>
        <div className="home-header-meta">
          <span className="status-chip">
            <HardDrive size={14} />
            本地安全处理，文件不出浏览器
          </span>
        </div>
      </header>

      <main className="home-main">
        <section
          className={`upload-zone ${dragging ? "is-dragging" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
        >
          <div className="upload-icon">
            <Upload size={28} strokeWidth={2} />
          </div>
          <div className="upload-copy">
            <h2>拖入文件，立即开始</h2>
            <p>支持 PDF、Word、Excel、PPT、图片与文本，所有处理都在本机浏览器完成</p>
          </div>
          <div className="upload-actions">
            <button type="button" className="btn btn-primary" onClick={() => openTool("")}>
              选择文件
              <Upload size={16} />
            </button>
            <button type="button" className="btn btn-quick" onClick={() => openTool("compress")}>
              <Archive size={16} />
              压缩文件
            </button>
            <button type="button" className="btn btn-quick" onClick={() => openTool("share")}>
              <QrCode size={16} />
              生成二维码
            </button>
            <button type="button" className="btn btn-quick" onClick={() => setMerging(true)}>
              <Link2 size={16} />
              合并 PDF
            </button>
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="visually-hidden"
            onChange={(e) => {
              const tool = e.currentTarget.dataset.tool || "";
              handleFiles(e.target.files, tool);
            }}
          />
        </section>

        <section className="home-section">
          <div className="section-head">
            <h3>支持的格式</h3>
            <span className="section-note">选择格式后打开工作台即可使用全部工具</span>
          </div>
          <div className="format-grid">
            {FORMATS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  key={item.key}
                  className={`format-row tone-${item.tone}`}
                  onClick={() => openTool("")}
                >
                  <span className="format-icon">
                    <Icon size={19} strokeWidth={2} />
                  </span>
                  <span className="format-copy">
                    <strong>{item.label}</strong>
                    <small>{item.desc}</small>
                  </span>
                  <ArrowUpRight size={16} className="format-arrow" />
                </button>
              );
            })}
          </div>
        </section>

        <section className="home-section">
          <div className="section-head">
            <h3>我的文档</h3>
            <span className="section-note">{metas.length ? `${metas.length} 个文件已保存在本机` : "上传后自动保存在本地文档库"}</span>
          </div>
          {metas.length ? (
            <div className="recent-table">
              <div className="recent-head">
                <span>文件名</span>
                <span>类型</span>
                <span>大小</span>
                <span>更新时间</span>
                <span>操作</span>
              </div>
              {metas.map((meta) => (
                <div className="recent-row" key={meta.id}>
                  <button
                    type="button"
                    className="recent-name"
                    onClick={() => onOpen(meta)}
                    title={`打开 ${meta.name}`}
                  >
                    <KindIcon kind={meta.kind} />
                    <span>{meta.name}</span>
                  </button>
                  <span className="kind-badge">{kindLabel(meta.kind)}</span>
                  <span className="muted-cell">{formatBytes(meta.size)}</span>
                  <span className="muted-cell">{formatDate(meta.updatedAt)}</span>
                  <span className="recent-actions">
                    <button type="button" className="icon-btn" title="打开" onClick={() => onOpen(meta)}>
                      <ArrowUpRight size={16} />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      title="分享"
                      onClick={() => onOpen(meta, "share")}
                    >
                      <Share2 size={16} />
                    </button>
                    <button
                      type="button"
                      className="icon-btn icon-btn-danger"
                      title="删除"
                      onClick={() => remove(meta.id, meta.name)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Files size={30} strokeWidth={1.7} />
              <p>还没有文档，上传第一个文件后这里会显示最近记录</p>
            </div>
          )}
        </section>

        <section className="home-section">
          <div className="section-head">
            <h3>云端文档</h3>
            <span className="section-note">
              {cloudReady === null && "正在连接云端服务..."}
              {cloudReady === false && "云端服务未连接，部署后可在工作台分享面板上传"}
              {cloudReady === true && `${cloudFiles.length} 个文件存储在 D1 + R2`}
            </span>
          </div>
          {cloudReady === true ? (
            cloudFiles.length ? (
              <div className="recent-table">
                <div className="recent-head">
                  <span>文件名</span>
                  <span>类型</span>
                  <span>大小</span>
                  <span>上传时间</span>
                  <span>操作</span>
                </div>
                {cloudFiles.map((meta) => (
                  <div className="recent-row" key={meta.id}>
                    <button
                      type="button"
                      className="recent-name"
                      onClick={() => openCloud(meta)}
                      title={`打开 ${meta.name}`}
                    >
                      <KindIcon kind={meta.kind} />
                      <span>{meta.name}</span>
                    </button>
                    <span className="kind-badge">{kindLabel(meta.kind)}</span>
                    <span className="muted-cell">{formatBytes(meta.size)}</span>
                    <span className="muted-cell">{formatDate(meta.createdAt)}</span>
                    <span className="recent-actions">
                      <button type="button" className="icon-btn" title="打开" onClick={() => openCloud(meta)}>
                        <ArrowUpRight size={16} />
                      </button>
                      <button type="button" className="icon-btn" title="下载" onClick={() => downloadCloud(meta)}>
                        <Download size={16} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="分享"
                        onClick={() => openCloud(meta, "share")}
                      >
                        <Share2 size={16} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn-danger"
                        title="删除"
                        onClick={() => removeCloud(meta)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <CloudIcon />
                <p>云端还没有文档，在工作台分享面板可上传到云端</p>
              </div>
            )
          ) : (
            <div className="empty-state">
              <CloudIcon />
              <p>部署到 Cloudflare 并配置 D1 + R2 后，这里会显示云端文档</p>
            </div>
          )}
        </section>
      </main>

      {merging && (
        <MergeModal
          onClose={() => setMerging(false)}
          onToast={onToast}
        />
      )}
    </div>
  );
}

function CloudIcon() {
  return <Upload size={30} strokeWidth={1.7} />;
}

function KindIcon({ kind }) {
  const map = {
    pdf: FileText,
    docx: File,
    xlsx: FileSpreadsheet,
    pptx: Presentation,
    image: FileImage,
    text: FileText,
    sheet: FileSpreadsheet,
  };
  const Icon = map[kind] || File;
  return <Icon size={16} strokeWidth={2} />;
}

function MergeModal({ onClose, onToast }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  async function add(e) {
    const picked = Array.from(e.target.files || []).filter((f) => kindFromName(f.name) === "pdf");
    if (picked.length !== e.target.files.length) onToast("合并 PDF 仅支持 PDF 文件", "error");
    const loaded = [];
    for (const file of picked) loaded.push({ name: file.name, buffer: await file.arrayBuffer() });
    setFiles((list) => [...list, ...loaded]);
  }

  async function run() {
    if (files.length < 2) {
      onToast("请至少选择两个 PDF 文件", "error");
      return;
    }
    setBusy(true);
    try {
      const out = await mergePdfs(files.map((f) => f.buffer));
      downloadBlob(new Blob([out], { type: "application/pdf" }), `合并文档-${Date.now()}.pdf`);
      onToast("PDF 合并完成，已开始下载", "success");
      onClose();
    } catch (err) {
      onToast(`合并失败：${err.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-label="合并 PDF">
        <div className="modal-head">
          <h3>合并 PDF</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="关闭">
            <X size={18} />
          </button>
        </div>
        <label className="file-pick">
          <Upload size={18} />
          <span>添加 PDF 文件</span>
          <input type="file" multiple accept=".pdf,application/pdf" onChange={add} />
        </label>
        {files.length > 0 && (
          <div className="merge-list">
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} className="merge-item">
                <span>{i + 1}</span>
                <strong>{f.name}</strong>
                <button
                  type="button"
                  className="icon-btn icon-btn-danger"
                  onClick={() => setFiles((list) => list.filter((_, x) => x !== i))}
                >
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="modal-foot">
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={run}
          >
            {busy ? "合并中..." : "开始合并"}
            {busy ? null : <Check size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
